import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { authFetch } from '../../api/client';

/** Route video URLs through our backend proxy to avoid CORS issues with Azure Blob Storage */
function proxyVideoUrl(url: string): string {
  const backendBase = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8005`;
  return `${backendBase}/api/media/proxy?url=${encodeURIComponent(url)}`;
}

// Module-level blob cache: stores blob URLs keyed by proxy URL.
// Survives component unmount/remount (React StrictMode, streaming re-renders).
const blobCache = new Map<string, string>();

interface VideoScrubPreviewProps {
  thumbnailUrl?: string;
  videoUrl: string;
  alt?: string;
}

/**
 * Video scrub preview: shows a poster frame, and on hover scrubs through the video.
 * Fetches video as a Blob so seeking always works (proxy lacks Range header support).
 */
const VideoScrubPreview: React.FC<VideoScrubPreviewProps> = ({ thumbnailUrl, videoUrl, alt }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoReadyRef = useRef(false);

  const [hovering, setHovering] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [posterUrl, setPosterUrl] = useState<string | null>(thumbnailUrl || null);

  const proxiedUrl = useMemo(() => proxyVideoUrl(videoUrl), [videoUrl]);

  // Fetch video as blob so it's fully seekable (blob URLs don't need Range headers).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const cachedBlob = blobCache.get(proxiedUrl);
    if (cachedBlob) {
      video.src = cachedBlob;
      video.load();
      return;
    }

    const controller = new AbortController();

    authFetch(proxiedUrl, { signal: controller.signal })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        blobCache.set(proxiedUrl, blobUrl);
        video.src = blobUrl;
        video.load();
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          // Fallback: direct proxy URL (seeking may not work immediately)
          video.src = proxiedUrl;
          video.load();
        }
      });

    return () => { controller.abort(); };
  }, [proxiedUrl]);

  const handleLoadedData = useCallback(() => {
    videoReadyRef.current = true;
    setVideoReady(true);
    if (!thumbnailUrl && videoRef.current) {
      videoRef.current.currentTime = 0.1;
    }
  }, [thumbnailUrl]);

  const handleMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || !videoRef.current) return;
    if (!videoReadyRef.current) return;

    const { left, width } = containerRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - left) / width));

    if (videoRef.current.duration && !isNaN(videoRef.current.duration)) {
      videoRef.current.currentTime = ratio * videoRef.current.duration;
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHovering(false);
    if (videoRef.current && videoReadyRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, []);

  const showVideo = (hovering && videoReady) || !posterUrl;

  return (
    <div
      ref={containerRef}
      className="video-scrub-container"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        onLoadedData={handleLoadedData}
        preload="auto"
        muted
        className="video-scrub-video"
        style={{
          opacity: showVideo ? 1 : 0,
          position: showVideo ? 'static' : 'absolute',
          pointerEvents: showVideo ? 'auto' : 'none',
        }}
      />

      {posterUrl && !showVideo && (
        <img
          src={posterUrl}
          alt={alt || 'Video preview'}
          className="video-scrub-image"
          onError={() => setPosterUrl(null)}
        />
      )}

      {!posterUrl && !videoReady && (
        <div className="video-scrub-placeholder">▶</div>
      )}

      <div className="video-scrub-badge">▶</div>
    </div>
  );
};

export default VideoScrubPreview;
